// API service for admin endpoints

import { getStoredAdminToken } from '../lib/auth'
import type { AnalyticsPeriod } from '../lib/analytics-period'

export interface ModelUsage {
  model: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  spend_usd: number | null
}

export interface User {
  user_id: string
  name?: string
  email?: string
  created_at: string
  last_active?: string
  session_count?: number
  message_count?: number
  lifetime_response_count?: number
  lifetime_token_count?: number
  spend_usd?: number | null
  has_unpriced_models?: boolean
  model_usage?: ModelUsage[]
  privacy_restricted?: boolean
  account_status?: 'Free Trial' | 'Helpbot' | 'Subscribed' | 'Staff'
  org_id?: string
  org_name?: string
}

export interface UserProfile extends User {
  updated_at?: string
  is_active?: boolean
  metadata?: Record<string, unknown>
  account_status: 'Free Trial' | 'Helpbot' | 'Subscribed' | 'Staff'
  terms_status: 'Acknowledged' | 'Not recorded'
  followup_contact_enabled: boolean
  followup_contact_configured: boolean
  privacy_restricted: boolean
  lifetime_sessions: number | null
  lifetime_messages: number | null
  lifetime_tokens: number | null
  period: AnalyticsPeriod
  period_sessions: number | null
  period_messages: number | null
  period_tokens: number | null
  period_spend_usd: number | null
  has_unpriced_models: boolean
  model_usage: ModelUsage[]
}

export interface Session {
  session_id: string
  user_id: string
  user_name?: string
  user_email?: string
  title?: string
  created_at: string
  updated_at: string
  message_count?: number
}

export interface Message {
  message_id: string
  session_id: string
  user_id: string
  content: string
  role: string
  created_at: string
}

export interface IpOrganization {
  id: string
  name: string
  ip_entries: string[]
  org_account_id: string | null
  remembers_org_name?: string | null
  remembers_customer_id?: string | null
  seat_limit: number
  seats_used: number
  seats_available: number
  over_capacity: boolean
  admin_count: number
  public_access_id: string
  access_url: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface IpOrgPayload {
  name: string
  ip_entries: string[]
  org_account_id: string
  is_active: boolean
}

export interface IpOrganizationDetail extends IpOrganization {
  seats: Array<{ id: string; assigned_email: string; status: string; assigned_at: string; last_login_at?: string | null }>
  admins: Array<{ id: string; email: string; created_at: string }>
  events: Array<{ id: string; event_type: string; email?: string | null; reason_code?: string | null; created_at: string }>
}

function normalizeIpOrganization(value: unknown): IpOrganization {
  const org = (value && typeof value === 'object' ? value : {}) as Partial<IpOrganization>
  return {
    ...org,
    ip_entries: Array.isArray(org.ip_entries) ? org.ip_entries : [],
    seat_limit: Number(org.seat_limit ?? 0),
    seats_used: Number(org.seats_used ?? 0),
    seats_available: Number(org.seats_available ?? 0),
    over_capacity: Boolean(org.over_capacity),
    admin_count: Number(org.admin_count ?? 0),
  } as IpOrganization
}

export interface EmailOrganization {
  id: string
  name: string
  allowed_emails: string[]
  allowed_domains: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface EmailOrgPayload {
  name: string
  allowed_emails: string[]
  allowed_domains: string[]
  is_active: boolean
}

export interface MultiUserOrganization {
  id: string
  remembers_customer_id: string
  remembers_record_number?: string | null
  name?: string | null
  status: string
  provisioning_status: 'pending_admin' | 'active' | 'suspended'
  seat_limit: number
  seats_used: number
  seats_available: number
  over_capacity: boolean
  admin_count: number
  sync_failures: number
  queued_count: number
  pending_invite_count: number
  created_at: string
  allow_realtime_voice?: boolean
  allow_file_uploads?: boolean
  allow_translation?: boolean
  allow_followup_contact?: boolean
  allowed_upload_extensions?: string[] | null
}

export interface OrganizationCapabilitiesPayload {
  realtime_voice: boolean
  file_uploads: boolean
  translation: boolean
  followup_contact?: boolean
  allowed_upload_extensions: string[] | null
}

export interface MultiUserAdmin {
  id: string
  email: string
  invited_by?: string | null
  accepted_at?: string | null
}

export interface MultiUserAdminInvite {
  id: string
  email: string
  status: string
  expires_at: string
  created_at: string
}

export interface MultiUserSeatInvite {
  id: string
  email_lock: string
  status: 'active'
  expires_at?: string | null
  created_at: string
  url: string
}

export interface MultiUserSharedInvite {
  id: string
  kind: 'bulk'
  email_lock: null
  domain_lock: string | null
  used_count: number
  max_uses: number | null
  expires_at?: string | null
  url: string
}

export interface MultiUserOrganizationDetail {
  org: MultiUserOrganization
  seat_limit: number
  seats_used: number
  seats_available: number
  admins: MultiUserAdmin[]
  admin_invites: MultiUserAdminInvite[]
  // Optional during rolling deploys where the admin bundle can update before
  // the backend adds this response field.
  seat_invites?: MultiUserSeatInvite[]
  shared_invites?: MultiUserSharedInvite[]
  roster: {
    queue_version: number
    capacity: { purchased: number; active: number; queued: number; available: number; over_capacity: boolean }
    members: Array<{
      id: string
      email: string
      state: 'active' | 'queued'
      qualification_source: string
      queue_rank?: number | null
      // Latest sign-in OR question asked, whichever is later. Null when the seat
      // has been assigned but never used.
      last_activity_at?: string | null
      last_login_at?: string | null
      relationship_sync_status: string
    }>
  }
  subscriptions: Array<{
    product_code: string
    product_name?: string | null
    seat_quantity: number
    status: string
    source_order_number: string
  }>
  sync_failures: Array<{
    id: string
    assigned_email: string
    desired_state: string
    attempt_count: number
    last_error?: string | null
  }>
}

export interface OrganizationReconciliationReport {
  report_version: string
  clean: boolean
  provider: { status: string }
  capacity: { purchased: number }
  memberships: { active: number; queued: number }
  relationships: { expected: number; locally_synced: number; provider_observed_active: number; outbound_failures: unknown[]; inbound_failures: unknown[] }
  entitlements: { missing_org_sources: string[]; stale_org_sources: string[] }
  next_queued_member?: { email: string; queue_rank?: number | null } | null
  drift: string[]
}

export interface RateLimitEntry {
  user_id: string
  name?: string
  email?: string
  access_tier: 'staff' | 'subscribed' | 'free_trial'
  daily_token_budget: number | null
  effective_token_budget: number | null
  consumed_tokens_today: number
  token_pct: number
  token_status: 'ok' | 'warn' | 'blocked'
  daily_response_budget: number | null
  effective_response_budget: number | null
  consumed_responses_today: number
  response_pct: number
  response_status: 'ok' | 'warn' | 'blocked'
  status: 'ok' | 'warn' | 'blocked'
}

export interface RateLimitDefaults {
  default_daily_token_budget: number | null
  default_daily_response_budget: number | null
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/admin`
  : '/api/admin'

// Attaches the admin bearer token (if any) on top of caller-supplied headers.
function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = getStoredAdminToken()
  return {
    ...(extra ?? {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// A 401 means the stored token is missing/expired/invalid — drop the session
// and let the AuthProvider swap in the login screen.
function reportIfUnauthorized(response: Response) {
  if (response.status === 401) {
    window.dispatchEvent(new Event('mate-admin-unauthorized'))
  }
}

// Fetch wrapper that injects the bearer token and flags 401s, used for calls
// that don't need the timeout/abort handling of fetchWithTimeout.
async function authedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, { ...options, headers: authHeaders(options.headers) })
  reportIfUnauthorized(response)
  return response
}

// Helper function with timeout
async function fetchWithTimeout(url: string, timeout = 15000) {
  console.log(`🔄 Fetching: ${url}`)
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    console.log(`⏰ Timeout after ${timeout}ms`)
    controller.abort()
  }, timeout)

  try {
    const response = await fetch(url, { signal: controller.signal, headers: authHeaders() })
    clearTimeout(timeoutId)
    console.log(`✅ Response received: ${response.status}`)
    reportIfUnauthorized(response)
    return response
  } catch (error) {
    clearTimeout(timeoutId)
    console.error('❌ Fetch error:', error)
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - server took too long to respond')
    }
    throw error
  }
}


/** One Help Bot exchange. Question and answer text is admin-only — the analytics
 *  pipeline carries this row's id and metadata, never its content. */
export interface HelpBotTranscript {
  id: string
  user_id: string | null
  visitor_id: string | null
  user_type: 'visitor' | 'nonmember' | 'member'
  question: string
  answer: string | null
  intent: string | null
  source_count: number
  input_tokens: number | null
  output_tokens: number | null
  total_tokens: number | null
  model_calls: number | null
  created_at: string
  email: string | null
  name: string | null
}

export interface HelpBotTranscriptPage {
  total: number
  /** Averages cover everything matching the filter, not just the page on screen. */
  token_spend: {
    avg_total_tokens: number | null
    avg_input_tokens: number | null
    avg_output_tokens: number | null
    avg_model_calls: number | null
    sum_total_tokens: number
  }
  transcripts: HelpBotTranscript[]
}

export interface UserPage {
  total: number
  limit: number
  offset: number
  users: User[]
}

export interface AnalyticsRow {
  [key: string]: string | number | boolean | ModelUsage[] | null
}

export interface AdminAnalytics {
  period: AnalyticsPeriod
  organization_usage: AnalyticsRow[]
  daily_query_volume: AnalyticsRow[]
  response_types: AnalyticsRow[]
  source_types: AnalyticsRow[]
  top_users: AnalyticsRow[]
  no_answer_queries: AnalyticsRow[]
  spend_by_organization: AnalyticsRow[]
  spend_by_user: AnalyticsRow[]
  source_click_categories: AnalyticsRow[]
  source_click_urls: AnalyticsRow[]
  source_click_resources: AnalyticsRow[]
  source_click_items: AnalyticsRow[]
}

export interface SubscriptionJob {
  kind: 'expiration' | 'provider'
  status: 'idle' | 'pending' | 'running' | 'completed' | 'failed'
  requested_at: string | null
  started_at: string | null
  finished_at: string | null
  requested_by: string | null
  error_count: number
  timezone: string
}

export const api = {
  async getSubscriptionJobs(): Promise<{ jobs: SubscriptionJob[]; provider_interval_seconds: number }> {
    const response = await fetchWithTimeout(`${API_BASE}/subscription-jobs`)
    if (!response.ok) throw new Error('Failed to load subscription maintenance status')
    return response.json()
  },

  async runSubscriptionJob(kind: SubscriptionJob['kind']): Promise<void> {
    const response = await authedFetch(`${API_BASE}/subscription-jobs/${kind}`, { method: 'POST' })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to start subscription maintenance')
    }
  },

  // Users
  /** One page of users plus the FULL match count, so the number on screen and
   *  the rows in the table cannot disagree. */
  async getUsers(params: { limit?: number; offset?: number; search?: string; org_id?: string; sort_by?: string; sort_direction?: 'asc' | 'desc'; period?: AnalyticsPeriod } = {}): Promise<UserPage> {
    try {
      const query = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) query.set(key, String(value))
      })
      const response = await fetchWithTimeout(`${API_BASE}/users?${query.toString()}`)

      const data = await response.json()

      // Check for error responses even with 200 status
      if (data.detail) {
        throw new Error(data.detail)
      }

      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`)
      }

      if (!data || !Array.isArray(data.users)) {
        console.error('Expected { total, users } but got:', data)
        throw new Error('Invalid response format from server')
      }

      return data as UserPage
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to server - please check if the backend is running')
      }
      throw error
    }
  },

  /** Fetch every matching user in bounded pages for exports. */
  async getAllUsers(params: { search?: string; org_id?: string; sort_by?: string; sort_direction?: 'asc' | 'desc'; period?: AnalyticsPeriod } = {}): Promise<User[]> {
    const users: User[] = []
    const limit = 200
    let offset = 0

    while (true) {
      const page = await api.getUsers({ ...params, limit, offset })
      users.push(...page.users)
      offset += page.users.length
      if (page.users.length === 0 || users.length >= page.total) break
    }

    return users
  },

  async getUser(userId: string, period: AnalyticsPeriod = 'lifetime'): Promise<UserProfile> {
    const response = await authedFetch(`${API_BASE}/users/${userId}?period=${period}`)
    if (!response.ok) throw new Error('Failed to fetch user')
    return response.json()
  },

  // Help Bot
  async getHelpBotTranscripts(params: {
    limit?: number
    offset?: number
    user_type?: string
    search?: string
    since_days?: number
    order_by?: 'recent' | 'tokens'
  } = {}): Promise<HelpBotTranscriptPage> {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) query.set(key, String(value))
    })
    const response = await authedFetch(`${API_BASE}/help-bot/transcripts?${query.toString()}`)
    if (!response.ok) {
      const detail = await response.json().catch(() => null)
      throw new Error(detail?.detail || 'Failed to fetch Help Bot transcripts')
    }
    return response.json()
  },

  // Sessions
  async getSessions(period: AnalyticsPeriod = 'lifetime'): Promise<Session[]> {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/sessions?period=${period}`)

      const data = await response.json()

      if (data.detail) {
        throw new Error(data.detail)
      }

      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`)
      }

      if (!Array.isArray(data)) {
        console.error('Expected array but got:', data)
        throw new Error('Invalid response format from server')
      }

      return data
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to server - please check if the backend is running')
      }
      throw error
    }
  },

  async getUserSessions(userId: string, period: AnalyticsPeriod = 'lifetime'): Promise<Session[]> {
    const response = await authedFetch(`${API_BASE}/users/${userId}/sessions?period=${period}`)
    if (!response.ok) throw new Error('Failed to fetch user sessions')
    const data = await response.json()
    return Array.isArray(data) ? data : data.sessions || []
  },

  // Messages
  async getSessionMessages(sessionId: string, userId: string): Promise<Message[]> {
    const response = await authedFetch(`${API_BASE}/sessions/${sessionId}/messages?user_id=${userId}`)
    if (!response.ok) throw new Error('Failed to fetch messages')
    const data = await response.json()
    // Backend returns {messages: [...], sources: [...]}
    return data.messages || []
  },

  // Downvotes
  async getDownvotes(period: AnalyticsPeriod = 'lifetime') {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/downvotes?period=${period}`)

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
      }

      const data = await response.json()

      if (data.detail) {
        throw new Error(data.detail)
      }

      if (!Array.isArray(data)) {
        console.error('Expected array but got:', data)
        throw new Error('Invalid response format from server')
      }

      return data
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to server - please check if the backend is running')
      }
      throw error
    }
  },

  // Stats
  async getStats(period: AnalyticsPeriod = 'lifetime') {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/stats?period=${period}`)

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
      }

      const data = await response.json()

      if (data.detail) {
        throw new Error(data.detail)
      }

      return data
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to server - please check if the backend is running')
      }
      throw error
    }
  },

  async getAnalytics(period: AnalyticsPeriod = 'lifetime'): Promise<AdminAnalytics> {
    const response = await fetchWithTimeout(`${API_BASE}/analytics?period=${period}`)
    if (!response.ok) {
      const detail = await response.json().catch(() => null)
      throw new Error(detail?.detail || 'Failed to fetch analytics')
    }
    return response.json()
  },

  // IP Organizations
  async getIpOrganizations(): Promise<IpOrganization[]> {
    const response = await fetchWithTimeout(`${API_BASE}/ip-organizations`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data: unknown = await response.json()
    if (!Array.isArray(data)) throw new Error('Invalid IP organizations response')
    return data.map(normalizeIpOrganization)
  },

  async createIpOrganization(body: IpOrgPayload): Promise<IpOrganization> {
    const response = await authedFetch(`${API_BASE}/ip-organizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  async getIpOrganization(id: string): Promise<IpOrganizationDetail> {
    const response = await fetchWithTimeout(`${API_BASE}/ip-organizations/${encodeURIComponent(id)}`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async addIpOrganizationAdmin(id: string, email: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${encodeURIComponent(id)}/admins`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
    })
    if (!response.ok) throw new Error(await response.text())
  },

  async removeIpOrganizationAdmin(orgId: string, adminId: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${encodeURIComponent(orgId)}/admins/${encodeURIComponent(adminId)}`, { method: 'DELETE' })
    if (!response.ok) throw new Error(await response.text())
  },

  async removeIpOrganizationSeat(orgId: string, seatId: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${encodeURIComponent(orgId)}/seats/${encodeURIComponent(seatId)}`, { method: 'DELETE' })
    if (!response.ok) throw new Error(await response.text())
  },

  async rotateIpOrganizationLink(id: string): Promise<{ public_access_id: string }> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${encodeURIComponent(id)}/rotate-link`, { method: 'POST' })
    if (!response.ok) throw new Error(await response.text())
    return response.json()
  },

  async updateIpOrganization(id: string, body: Partial<IpOrgPayload>): Promise<IpOrganization> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  async deleteIpOrganization(id: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/ip-organizations/${id}`, { method: 'DELETE' })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
  },

  // Email Organizations
  async getEmailOrganizations(): Promise<EmailOrganization[]> {
    const response = await fetchWithTimeout(`${API_BASE}/email-organizations`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async createEmailOrganization(body: EmailOrgPayload): Promise<EmailOrganization> {
    const response = await authedFetch(`${API_BASE}/email-organizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  async updateEmailOrganization(id: string, body: Partial<EmailOrgPayload>): Promise<EmailOrganization> {
    const response = await authedFetch(`${API_BASE}/email-organizations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  async deleteEmailOrganization(id: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/email-organizations/${id}`, { method: 'DELETE' })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
  },

  // Mat-E Multi-User organizations
  async getMultiUserOrganizations(): Promise<MultiUserOrganization[]> {
    const response = await fetchWithTimeout(`${API_BASE}/multi-user`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async getMultiUserOrganization(id: string): Promise<MultiUserOrganizationDetail> {
    const response = await fetchWithTimeout(`${API_BASE}/multi-user/${encodeURIComponent(id)}`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async updateMultiUserOrganizationCapabilities(
    id: string,
    body: OrganizationCapabilitiesPayload,
  ): Promise<OrganizationCapabilitiesPayload> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(id)}/capabilities`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  async createMultiUserAdminInvite(
    orgId: string,
    email: string,
    sendEmail: boolean,
  ): Promise<{ invite: MultiUserAdminInvite; url: string; email_sent: boolean; email_error?: string | null }> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/admin-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, send_email: sendEmail }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  async revokeMultiUserAdminInvite(orgId: string, inviteId: string): Promise<void> {
    const response = await authedFetch(
      `${API_BASE}/multi-user/${encodeURIComponent(orgId)}/admin-invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
  },

  // allowFinal removes the organization's LAST admin, leaving it with none. The
  // backend refuses without it, so the caller has to have asked first.
  async removeMultiUserAdmin(orgId: string, adminId: string, allowFinal = false): Promise<void> {
    const response = await authedFetch(
      `${API_BASE}/multi-user/${encodeURIComponent(orgId)}/admins/${encodeURIComponent(adminId)}` +
        (allowFinal ? '?allow_final=true' : ''),
      { method: 'DELETE' },
    )
    const data = response.status === 204 ? {} : await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
  },

  async assignMultiUserSeat(orgId: string, email: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/seats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
  },

  async createMultiUserSeatInvite(
    orgId: string,
    email: string,
    sendEmail: boolean,
  ): Promise<{ invite: { id: string; email_lock: string }; url: string; email_sent: boolean; email_error?: string | null; will_queue: boolean; seats_available: number }> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/seat-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, send_email: sendEmail }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  async createSharedMemberInvite(orgId: string, domainLock: string): Promise<{ invite: MultiUserSharedInvite; url: string }> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/shared-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain_lock: domainLock.trim() || null }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  async revokeSharedMemberInvite(orgId: string, inviteId: string): Promise<void> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/shared-invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.detail || `HTTP ${response.status}`)
    }
  },

  async revokeMultiUserSeat(orgId: string, seatId: string): Promise<void> {
    const response = await authedFetch(
      `${API_BASE}/multi-user/${encodeURIComponent(orgId)}/seats/${encodeURIComponent(seatId)}`,
      { method: 'DELETE' },
    )
    const data = response.status === 204 ? {} : await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
  },

  async reorderMultiUserQueue(orgId: string, memberIds: string[], expectedVersion: number): Promise<void> {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/roster/queue`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_ids: memberIds, expected_version: expectedVersion }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
  },

  async retryMultiUserRelationshipSync(jobId: string): Promise<{ seat_id: string; synced: boolean }> {
    const response = await authedFetch(
      `${API_BASE}/multi-user/relationship-sync/${encodeURIComponent(jobId)}/retry`,
      { method: 'POST' },
    )
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  async getOrganizationReconciliation(orgId: string): Promise<OrganizationReconciliationReport> {
    const response = await fetchWithTimeout(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/reconciliation`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async reconcileOrganization(orgId: string, report: OrganizationReconciliationReport, reason: string) {
    const response = await authedFetch(`${API_BASE}/multi-user/${encodeURIComponent(orgId)}/reconciliation`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        confirmation: orgId,
        expected_report_version: report.report_version,
        reason,
        request_id: crypto.randomUUID(),
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`)
    return data
  },

  // System Settings
  async getSystemSettings(): Promise<SystemSettings> {
    const response = await fetchWithTimeout(`${API_BASE}/system-settings`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async updateSystemSettings(patch: Partial<SystemSettings>): Promise<SystemSettings> {
    const response = await authedFetch(`${API_BASE}/system-settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  // Rate Limits
  async getRateLimits(): Promise<RateLimitEntry[]> {
    const response = await fetchWithTimeout(`${API_BASE}/rate-limits`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async updateRateLimit(userId: string, patch: { daily_token_budget?: number | null; daily_response_budget?: number | null }): Promise<void> {
    const response = await authedFetch(`${API_BASE}/rate-limits/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
  },

  async getGlobalDefaults(): Promise<RateLimitDefaults> {
    const response = await fetchWithTimeout(`${API_BASE}/rate-limits/defaults`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async updateGlobalDefaults(patch: { default_daily_token_budget?: number | null; default_daily_response_budget?: number | null }): Promise<RateLimitDefaults> {
    const response = await authedFetch(`${API_BASE}/rate-limits/defaults`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP ${response.status}: ${text}`)
    }
    return response.json()
  },

  // ─── Source of Truth (ASM Knowledge Base) ───
  async getSourceOfTruth(): Promise<{ facts: SotFact[]; count: number }> {
    const response = await fetchWithTimeout(`${API_BASE}/source-of-truth`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  async upsertSourceOfTruth(fact: SotFactInput): Promise<{ fact_id: string }> {
    const response = await authedFetch(`${API_BASE}/source-of-truth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fact),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }))
      throw new Error(err.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  async deleteSourceOfTruth(factId: string): Promise<{ deleted: string }> {
    const response = await authedFetch(`${API_BASE}/source-of-truth/${encodeURIComponent(factId)}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }))
      throw new Error(err.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  async fetchUrlForSot(url: string): Promise<{ url: string; title: string; text: string; truncated: boolean }> {
    const response = await authedFetch(`${API_BASE}/fetch-url?url=${encodeURIComponent(url)}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }))
      throw new Error(err.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },
}

export interface SotFact {
  fact_id: string
  topic: string
  owner: string
  question: string
  answer: string
  status: string
  url: string
  source_doc: string
}

export type SotFactInput = Omit<SotFact, 'fact_id'>

export interface SystemSettings {
  default_daily_token_budget: number | null
  default_daily_response_budget: number | null
  freemium_response_limit: number | null
}
